import {useParams} from 'react-router-dom'
import useStore from 'base/store'
import styles from './DocumentsDrawdowns.module.scss'

import {
  DocumentsContainer,
  DocumentsList,
  useGetDocuments,
  useDocumentsTabs,
  useDrawdownsTabs,
  useAMLTabs,
  useDocumentsFilter,
  DOCUMENTS_CATEGORY,
  DOCUMENTS_COLLECTION,
  IDocumentsTab,
  IDocument,
  TDocumentsCollection,
} from '..'

interface SubCategoryProps {
  order: number
  projectUuid: string
  modelUuid: string
  collection: string
  title: string
  description?: string
  tabOrder: number
  search: string
  documents: IDocument[]
  tabs: IDocumentsTab[]
  amlTabs: IDocumentsTab[]
}

export default function DocumentsDrawdowns() {
  const role = useStore((state) => state.role)
  const params = useParams()
  const {data: documents} = useGetDocuments(params.uuid)
  const {drawdownsTabs, drawdownsDocuments} = useDrawdownsTabs(documents)
  const [activeTab, setActiveTab] = useDocumentsTabs(drawdownsTabs)
  const {filteredDocuments, search, sort, setSearch, setSort} =
    useDocumentsFilter(drawdownsDocuments, activeTab?.collection, true)
  const {amlTabs} = useAMLTabs(params.uuid, undefined, (role?.user === true))

  if (!drawdownsTabs.length) return null

  return (
    <DocumentsContainer
      categoryKey="drawdowns"
      tabs={drawdownsTabs}
      activeTab={activeTab}
      modelUuid={activeTab.collection}
      modelType="drawdown"
      projectUuid={params.uuid}
      search={search}
      sort={sort}
      setSearch={setSearch}
      setSort={setSort}
      setActiveTab={setActiveTab}
    >
      <SubCategory
        order={1}
        projectUuid={params.uuid}
        modelUuid={activeTab.collection}
        collection={DOCUMENTS_COLLECTION.invoices.key}
        title={DOCUMENTS_COLLECTION.invoices.label}
        tabOrder={activeTab.order}
        search={search}
        documents={filteredDocuments.filter(
          (document) =>
            document.collection === DOCUMENTS_COLLECTION.invoices.key
        )}
        tabs={drawdownsTabs}
        amlTabs={(role?.user === true) ? undefined : amlTabs}
      />
      <SubCategory
        order={2}
        projectUuid={params.uuid}
        modelUuid={activeTab.collection}
        collection={DOCUMENTS_COLLECTION.buildingInspections.key}
        title="Certificates"
        description={`Incl. ${certificatesLabel}`}
        tabOrder={activeTab.order}
        search={search}
        documents={filteredDocuments.filter((document) =>
          certificatesKeys.includes(document.collection as TDocumentsCollection)
        )}
        tabs={drawdownsTabs}
        amlTabs={amlTabs}
      />
      <SubCategory
        order={3}
        projectUuid={params.uuid}
        modelUuid={activeTab.collection}
        collection={DOCUMENTS_COLLECTION.developmentSitePhotos.key}
        title="Photos"
        tabOrder={activeTab.order}
        search={search}
        documents={filteredDocuments.filter(
          (document) =>
            document.collection ===
            DOCUMENTS_COLLECTION.developmentSitePhotos.key
        )}
        tabs={drawdownsTabs}
        amlTabs={amlTabs}
      />
      <SubCategory
        order={4}
        projectUuid={params.uuid}
        modelUuid={activeTab.collection}
        collection={DOCUMENTS_COLLECTION.drawdownNotices.key}
        title="Drawdown notice"
        tabOrder={activeTab.order}
        search={search}
        documents={filteredDocuments.filter(
          (document) =>
            document.collection === DOCUMENTS_COLLECTION.drawdownNotices.key
        )}
        tabs={drawdownsTabs}
        amlTabs={amlTabs}
      />
    </DocumentsContainer>
  )
}

const SubCategory = ({
  projectUuid,
  modelUuid,
  collection,
  order,
  title,
  description,
  tabOrder,
  search,
  documents,
  tabs,
  amlTabs,
}: SubCategoryProps) => {
  return (
    <div className={styles.category}>
      <DocumentsList
        columns={3}
        projectUuid={projectUuid}
        modelUuid={modelUuid}
        modelType="drawdown"
        collection={collection}
        search={search}
        title={`${DOCUMENTS_CATEGORY.drawdowns.order}.${tabOrder}.${order} ${title}`}
        description={description}
        documents={documents}
        drawdownsTabs={tabs}
        amlTabs={amlTabs}
      />
    </div>
  )
}

const certificatesCollections = [
  DOCUMENTS_COLLECTION.buildingInspections,
  DOCUMENTS_COLLECTION.geotechAndSurveyReports,
  DOCUMENTS_COLLECTION.engineerReports,
  DOCUMENTS_COLLECTION.utilityCertificationAndCompliance,
  DOCUMENTS_COLLECTION.qsReports,
  DOCUMENTS_COLLECTION.cccAndTitles,
  DOCUMENTS_COLLECTION.preSales,
]

const certificatesLabel = certificatesCollections
  .map((collection) => collection.label)
  .join(', ')

const certificatesKeys = certificatesCollections.map(
  (collection) => collection.key
)
